import { computed, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  AI_GATEWAY,
  WHAiService,
  WHLoginDataService,
  AiSessionPageable,
  IWHAiConfigModel,
  IWHAiSuggestionModel,
  IWHFeedbackModel,
} from '@workheld/workheld-shared-lib';
import {
  debounceTime,
  finalize,
  map,
  Observable,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { handleGlobalError } from 'src/app/app-shared/operators/global-error.operator';
import { environment } from 'src/environments/environment';
import {
  buildAiHtmlResponse,
  extractTextAndAnnotations,
  getSourcesLabel,
} from './config/wh-ai-response.utils';

const DEFAULT_TENANT_TOKENS = ['workheldflowlivetest', 'workheld'];

type AiState = {
  pastSessions: AiSessionPageable | null;
  activeSessionId: string | null;
  activeSessionChatHistory: any[];
  suggestions: IWHAiSuggestionModel[];
  params: { page: number; size: number };
  endpointConfig: IWHAiConfigModel;
  isAiActive: boolean;
  isSessionHistoryActive: boolean;
  isLoadingSessions: boolean;
  isLoadingChatHistory: boolean;
  isLoadingSuggestions: boolean;
  downloadingDocUrls: Set<string>;
};

const initialState: AiState = {
  pastSessions: null,
  activeSessionId: null,
  activeSessionChatHistory: [],
  suggestions: [],
  params: { page: 0, size: 35 },
  endpointConfig: {
    apiUrl: environment.apiUrl + AI_GATEWAY,
  },
  isAiActive: false,
  isSessionHistoryActive: false,
  isLoadingSessions: false,
  isLoadingChatHistory: false,
  isLoadingSuggestions: false,
  downloadingDocUrls: new Set<string>(),
};

export const AiStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store, loginDataService = inject(WHLoginDataService)) => ({
    hasMoreSessions: computed(() => store.pastSessions()?.last === false),
    currentPage: computed(() => store.pastSessions()?.number ?? -1),
    isDefaultTenant: computed(() =>
      DEFAULT_TENANT_TOKENS.includes(
        loginDataService.activeTenantDOM$?.value?.token,
      ),
    ),
  })),
  withMethods(
    (
      store,
      aiService = inject(WHAiService),
      httpClient = inject(HttpClient),
      snackBar = inject(MatSnackBar),
      loginDataService = inject(WHLoginDataService),
      translateService = inject(TranslateService),
    ) => ({
      setActiveSessionId(sessionId: string | null): void {
        patchState(store, { activeSessionId: sessionId });
      },
      setIsAiActive(isActive: boolean): void {
        patchState(store, { isAiActive: isActive });
      },
      setPage(page: number): void {
        patchState(store, {
          params: { ...store.params(), page },
        });
      },
      toggleAiActive(): void {
        patchState(store, { isAiActive: !store.isAiActive() });
      },
      toggleIsSessionHistoryActive(): void {
        patchState(store, {
          isSessionHistoryActive: !store.isSessionHistoryActive(),
        });
      },
      createNewSession(): Observable<string> {
        return aiService.createNewSession(store.endpointConfig()).pipe(
          tap((sessionId) => {
            sessionStorage.setItem(
              'wh-ai-session',
              JSON.stringify({ sessionId: sessionId, timestamp: Date.now() }),
            );
            patchState(store, {
              activeSessionId: sessionId,
            });
          }),
        );
      },
      resetSession(): void {
        sessionStorage.removeItem('wh-ai-session');

        patchState(store, {
          activeSessionId: null,
          isSessionHistoryActive: false,
          activeSessionChatHistory: [],
        });
      },
      loadSuggestions: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoadingSuggestions: true })),
          switchMap(() =>
            aiService.getSuggestions(store.endpointConfig()).pipe(
              tap((suggestions) => {
                patchState(store, {
                  suggestions,
                  isLoadingSuggestions: false,
                });
              }),
              handleGlobalError({
                onError: () =>
                  patchState(store, { isLoadingSuggestions: false }),
              }),
            ),
          ),
        ),
      ),
      loadUserSessions: rxMethod<{
        page: number;
        size: number;
        reset?: boolean;
      }>(
        pipe(
          tap(() => patchState(store, { isLoadingSessions: true })),
          switchMap(({ page, size, reset }) =>
            aiService
              .getUserSessions(store.endpointConfig(), { page, size })
              .pipe(
                tap((response) => {
                  patchState(store, (state) => ({
                    isLoadingSessions: false,
                    pastSessions:
                      reset || !state.pastSessions
                        ? response
                        : {
                            ...response,
                            content: [
                              ...state.pastSessions.content,
                              ...response.content,
                            ],
                          },
                  }));
                }),
                handleGlobalError({
                  onError: () =>
                    patchState(store, { isLoadingSessions: false }),
                }),
              ),
          ),
        ),
      ),
      loadChatHistory: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoadingChatHistory: true,
            }),
          ),
          debounceTime(400),
          switchMap((sessionId) => {
            const config = { ...store.endpointConfig(), sessionId };

            return aiService.getSessionChatHistory(config).pipe(
              map((chatHistory) => {
                const deepChatMessages = chatHistory.map((msg, index) => {
                  if (msg.role === 'assistant') {
                    const questionCandidate = chatHistory[index - 1];
                    const questionText = questionCandidate
                      ? extractTextAndAnnotations(questionCandidate).text
                      : '';
                    return {
                      role: 'ai',
                      html: buildAiHtmlResponse(msg, {
                        runId: msg.runId,
                        questionText,
                        createdAt: msg.createdAt,
                        locale:
                          translateService.currentLang ||
                          translateService.defaultLang,
                        enableFeedback: true,
                        sourcesLabel: getSourcesLabel(translateService),
                      }),
                    };
                  }

                  return {
                    role: 'user',
                    text: extractTextAndAnnotations(msg).text,
                  };
                });
                return deepChatMessages || [];
              }),
              tap((chatHistory) => {
                patchState(store, {
                  activeSessionId: sessionId,
                  activeSessionChatHistory: chatHistory,
                  isLoadingChatHistory: false,
                });
              }),
            );
          }),
          handleGlobalError({
            onError: () => patchState(store, { isLoadingChatHistory: false }),
          }),
        ),
      ),
      loadMoreSessions(): void {
        if (store.isLoadingSessions() || !store.hasMoreSessions()) {
          return;
        }

        const nextPage = store.currentPage() + 1;
        patchState(store, { isLoadingSessions: true });
        aiService
          .getUserSessions(store.endpointConfig(), {
            page: nextPage,
            size: store.params().size,
          })
          .pipe(
            handleGlobalError({
              onError: () => patchState(store, { isLoadingSessions: false }),
            }),
          )
          .subscribe((response) => {
            patchState(store, (state) => ({
              isLoadingSessions: false,
              pastSessions: {
                ...response,
                content: [
                  ...(state.pastSessions?.content ?? []),
                  ...response.content,
                ],
              },
            }));
          });
      },
      downloadDocumentFromUrl(documentUrl: string): void {
        const tenantToken = loginDataService.activeTenantDOM$?.value?.token;
        const path = stripDocumentBaseUrl(documentUrl, tenantToken);

        patchState(store, {
          downloadingDocUrls: new Set([
            ...store.downloadingDocUrls(),
            documentUrl,
          ]),
        });

        getDocumentBlobByPath(httpClient, store.endpointConfig(), path)
          .pipe(
            handleGlobalError(),
            tap((response) => {
              if (!response || response.size === 0) {
                showDocumentOpenFailedSnackbar(snackBar, translateService);
                return;
              }

              const blobUrl = URL.createObjectURL(response);
              window.open(blobUrl, '_blank', 'noopener');
              window.setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
            }),
            finalize(() => {
              const next = new Set(store.downloadingDocUrls());
              next.delete(documentUrl);
              patchState(store, { downloadingDocUrls: next });
            }),
          )
          .subscribe();
      },
      sendFeedback(feedback: IWHFeedbackModel): void {
        aiService
          .sendFeedback(store.endpointConfig(), feedback)
          .pipe(handleGlobalError())
          .subscribe();
      },
    }),
  ),
);

export function stripDocumentBaseUrl(
  documentUrl: string,
  tenantToken?: string | null,
): string {
  const baseUrl = 'https://workheldai.blob.core.windows.net';
  if (!documentUrl) {
    return '';
  }

  const trimmed = documentUrl.trim();
  const normalizedBase = baseUrl.replace(/\/$/, '');
  let path = '';

  if (trimmed.startsWith(normalizedBase)) {
    path = trimmed.slice(normalizedBase.length);
  } else if (trimmed.includes('core.windows.net')) {
    const index = trimmed.indexOf('core.windows.net');
    path = trimmed.slice(index + 'core.windows.net'.length);
  } else {
    path = trimmed;
  }

  path = path.replace(/^\//, '');

  if (tenantToken) {
    const normalizedToken = tenantToken.trim().toLowerCase();
    const segments = path.split('/');
    if (segments[0]?.toLowerCase() === normalizedToken) {
      segments.shift();
      path = segments.join('/');
    }
  }

  return decodePathSafely(path);
}

function decodePathSafely(path: string): string {
  if (!path) {
    return '';
  }

  try {
    // decodeURI preserves reserved separators like %2F, preventing path shape changes.
    return decodeURI(path);
  } catch {
    return path;
  }
}

function getDocumentBlobByPath(
  httpClient: HttpClient,
  endpointConfig: IWHAiConfigModel,
  path: string,
): Observable<Blob> {
  const baseUrl = endpointConfig.apiUrl?.replace(/\/$/, '') ?? '';
  const endpoint = `${baseUrl}/documents`;
  const normalizedPath = normalizeDocumentUrlParam(path);
  const params = new HttpParams().set('documentUrl', normalizedPath);
  return httpClient.get(endpoint, { params, responseType: 'blob' });
}

function normalizeDocumentUrlParam(path: string): string {
  if (!path) {
    return '';
  }

  try {
    // decodeURI preserves reserved separators like %2F, preventing path shape changes.
    return decodeURI(path);
  } catch {
    return path;
  }
}

function showDocumentOpenFailedSnackbar(
  snackBar: MatSnackBar,
  translateService: TranslateService,
): void {
  const key = 'ERROR.TASK_TE_WORKORDER_FILE_1';
  const fallbackKey = 'ERROR.GENERIC';
  const translated = translateService.instant(key)?.trim();
  const fallbackTranslated = translateService.instant(fallbackKey)?.trim();
  const message =
    translated && translated !== key
      ? translated
      : fallbackTranslated && fallbackTranslated !== fallbackKey
        ? fallbackTranslated
        : 'Unable to open document.';

  snackBar.open(message, '', { duration: 5000 });
}
